import { AppSidebar } from "@/components/app-sidebar";
import { MobileBottomNav } from "@/components/mobile-bottom-nav";
import { VersionRibbon } from "@/components/version-ribbon";
import { NavUser } from "@/components/nav-user";
import { SystemBanner } from "@/components/system-banner";
import { NotificationPopup } from "@/components/notification-popup";
import {
    SidebarInset,
    SidebarProvider,
    SidebarTrigger,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserNotificationSummary } from "@/hooks/use-user-notifications";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Roles } from "@/auth/user-roles";
import { LocalRoutes } from "@/LocalRoutes";
import { useTranslation } from "react-i18next";
import { Bell, ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { cn } from "@/lib/utils";

export default function SidebarLayout() {
    const { userInfo, logoutMutation } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { data: notificationSummary } = useUserNotificationSummary();
    const isMobile = useIsMobile();

    const getPageTitle = () => {
        switch (location.pathname) {
            case LocalRoutes.DASHBOARD:
                return t("routes.dashboard");
            case LocalRoutes.METERING_POINTS:
                return t("routes.metering-points");
            case LocalRoutes.BILLING:
                return t("routes.billing");
            case LocalRoutes.DATA_EXPORTS:
                return t("routes.data-exports");
            case LocalRoutes.INVOICES:
                return t("routes.invoices");
            case LocalRoutes.ENERGY_DATA:
                return t("routes.energy-data");
            case LocalRoutes.USER_DATA:
                return t("routes.user-data");
            case LocalRoutes.LEGAL_PERSONS:
                return t("routes.legal-persons");
            case LocalRoutes.ADMIN_DASHBOARD:
                return t("routes.g_admin.dashboard");
            case LocalRoutes.ADMIN_ENERGY_COMMUNITIES:
                return t("routes.g_admin.energy-communities");
            case LocalRoutes.ADMIN_ENERGY_COMMUNITY_MEMBER_CONTRACTS:
                return t("routes.g_admin.energy-community-member-contracts");
            case LocalRoutes.ADMIN_METERING_POINTS:
                return t("routes.g_admin.metering-points");
            case LocalRoutes.ADMIN_METERING_POINT_CONTRACTS:
                return t("routes.g_admin.metering-point-contracts");
            case LocalRoutes.ADMIN_USERS:
                return t("routes.g_admin.users");
            case LocalRoutes.ADMIN_LEGAL_PERSONS:
                return t("routes.g_admin.legal-persons");
            case LocalRoutes.ADMIN_PAYMENT_INFOS:
                return t("routes.g_admin.payment-infos");
            case LocalRoutes.ADMIN_SEPA_MANDATES:
                return t("routes.g_admin.sepa-mandates");
            case LocalRoutes.ADMIN_DOCUMENTS:
                return t("routes.g_admin.documents");
            case LocalRoutes.ADMIN_GRID_OPERATORS:
                return t("routes.g_admin.grid-operators");
            case LocalRoutes.ADMIN_PERMISSIONS:
                return t("routes.g_admin.permissions");
            case LocalRoutes.ADMIN_SYSTEM_BANNER:
                return t("routes.g_root.system-banner");
            case LocalRoutes.ADMIN_NOTIFICATIONS:
                return t("routes.g_admin.notifications");
            case LocalRoutes.ADMIN_ONBOARDING_CONTROLLING:
                return t("routes.g_admin.onboarding-controlling");
            case LocalRoutes.ADMIN_MESSAGE_INTERVENTIONS:
                return t("routes.g_admin.message-interventions");
            case LocalRoutes.ADMIN_INVOICES:
                return t("routes.g_admin.invoices");
            case LocalRoutes.ADMIN_INVOICE_CONTROLLING:
                return t("routes.g_admin.invoice-controlling");
            case LocalRoutes.ADMIN_BILLING_BATCH_LOG:
                return t("routes.g_admin.billing-batch-log");
            case LocalRoutes.ADMIN_BANK_TRANSACTIONS:
                return t("routes.g_admin.bank-transactions");
            case LocalRoutes.ADMIN_TARIFF_COMPONENTS:
                return t("routes.g_admin.tariff-components");
            case LocalRoutes.ADMIN_TARIFF_PACKAGES:
                return t("routes.g_admin.tariff-packages");
            case LocalRoutes.ADMIN_TARIFF_ASSIGNMENTS:
                return t("routes.g_admin.tariff-assignments");
            case LocalRoutes.ADMIN_TARIFF_VIEW:
                return t("routes.g_admin.tariff-view");
            case LocalRoutes.NOTIFICATIONS:
                return t("routes.notifications");
            case LocalRoutes.MEMBER_CONTRACTS:
                return t("routes.member-contracts");
            case LocalRoutes.CONTRACT_DOCUMENTS:
                return t("routes.contract-documents");
            case LocalRoutes.METERING_POINT_CONTRACTS:
                return t("routes.metering-point-contracts");
            case LocalRoutes.DEBUG_EDA_PROCESSES:
                return t("routes.g_debug.eda-processes");
            case LocalRoutes.DEBUG_EDA_MESSAGES:
                return t("routes.g_debug.eda-messages");
            case LocalRoutes.UNAUTHORIZED:
                return t("pages.unauthorized.title");
            default:
                if (location.pathname.startsWith("/energy-data/")) {
                    return t("routes.energy-data");
                }
                if (location.pathname.startsWith("/admin/tariff-components/")) {
                    return t("routes.g_admin.tariff-components");
                }
                if (location.pathname.startsWith("/admin/tariff-packages/")) {
                    return t("routes.g_admin.tariff-packages");
                }
                if (location.pathname.startsWith("/admin/tariff-view/")) {
                    return t("routes.g_admin.tariff-view-mpc");
                }
                if (
                    location.pathname.match(
                        /^\/admin\/metering-point-contracts\/\d+\/processes$/,
                    )
                ) {
                    return t("pages.admin.process-history.title");
                }
                if (
                    location.pathname.match(
                        /^\/admin\/energy-communities\/\d+\/billing$/,
                    )
                ) {
                    return t("pages.admin.billing.eg-title");
                }
                if (
                    location.pathname.match(
                        /^\/admin\/energy-community-member-contracts\/\d+\/billing$/,
                    )
                ) {
                    return t("pages.admin.billing.membership-title");
                }
                return "";
        }
    };

    const pageTitle = getPageTitle();
    useEffect(() => {
        document.title = pageTitle ? `${pageTitle} - eg.info` : "eg.info";
    }, [pageTitle]);

    const userMainPages: string[] = [
        LocalRoutes.USER_DATA,
        LocalRoutes.LEGAL_PERSONS,
        LocalRoutes.BILLING,
    ];

    const showBackButton =
        location.pathname.match(
            /^\/admin\/metering-point-contracts\/\d+\/processes$/,
        ) ||
        location.pathname.match(
            /^\/admin\/energy-communities\/\d+\/billing$/,
        ) ||
        location.pathname.match(
            /^\/admin\/energy-community-member-contracts\/\d+\/billing$/,
        ) ||
        location.pathname.startsWith("/admin/tariff-components/") ||
        location.pathname.startsWith("/admin/tariff-packages/") ||
        location.pathname.startsWith("/admin/tariff-view/") ||
        location.pathname.startsWith("/energy-data/") ||
        location.pathname === LocalRoutes.CONTRACT_DOCUMENTS ||
        location.pathname === LocalRoutes.METERING_POINT_CONTRACTS ||
        location.pathname === LocalRoutes.INVOICES ||
        userMainPages.includes(location.pathname);

    return (
        <>
            <VersionRibbon />
            <SidebarProvider defaultOpen={false}>
                <AppSidebar />
                <SidebarInset
                    className={cn(
                        "h-svh max-h-svh min-w-0",
                        isMobile &&
                            "landscape:pl-[calc(4rem+env(safe-area-inset-left))]",
                    )}
                >
                    <SystemBanner />
                    <NotificationPopup />
                    <header
                        className={cn(
                            "flex flex-row justify-between h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear min-w-0",
                            isMobile ? "pr-2" : "pr-4",
                        )}
                    >
                        <div
                            className={cn(
                                "flex items-center gap-2 flex-1 min-w-0",
                                isMobile ? "px-2" : "px-4",
                            )}
                        >
                            {!isMobile ? (
                                <SidebarTrigger className="-ml-1" />
                            ) : (
                                <Link
                                    aria-label={t("common.go-to-dashboard")}
                                    className="shrink-0"
                                    to={LocalRoutes.DASHBOARD}
                                >
                                    <img
                                        alt=""
                                        className="size-8"
                                        src="/logo_square.svg"
                                    />
                                </Link>
                            )}
                            {showBackButton ? (
                                <Button
                                    aria-label={t("common.back")}
                                    onClick={() => {
                                        const historyState: unknown =
                                            window.history.state;
                                        const idx =
                                            typeof historyState === "object" &&
                                            historyState !== null &&
                                            "idx" in historyState &&
                                            typeof historyState.idx === "number"
                                                ? historyState.idx
                                                : 0;
                                        if (idx > 0) {
                                            void navigate(-1);
                                        } else {
                                            void navigate("/");
                                        }
                                    }}
                                    size="icon"
                                    variant="outline"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                </Button>
                            ) : null}
                            {pageTitle ? (
                                <h1 className="text-lg sm:text-xl font-semibold truncate">
                                    {pageTitle}
                                </h1>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                aria-label={t("routes.notifications")}
                                className="relative"
                                onClick={() =>
                                    void navigate(LocalRoutes.NOTIFICATIONS)
                                }
                                size="icon"
                                variant="outline"
                            >
                                <Bell className="h-5 w-5" />
                                {notificationSummary &&
                                notificationSummary.unreadCount > 0 ? (
                                    <Badge
                                        className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                                        variant="destructive"
                                    >
                                        {notificationSummary.unreadCount > 9
                                            ? "9+"
                                            : notificationSummary.unreadCount}
                                    </Badge>
                                ) : null}
                            </Button>
                            <NavUser
                                logout={() => logoutMutation.mutate()}
                                user={{
                                    name: `${userInfo?.firstName} ${userInfo?.lastName}`,
                                    email: `${userInfo?.email}`,
                                    avatar: "",
                                    isAdmin:
                                        !!userInfo?.roles &&
                                        (userInfo.roles.includes(Roles.ADMIN) ||
                                            userInfo.roles.includes(
                                                Roles.ROOT,
                                            ) ||
                                            userInfo.roles.includes(
                                                Roles.SUPER_ROOT_3000,
                                            )),
                                }}
                            />
                        </div>
                    </header>
                    <div
                        className={cn(
                            "flex flex-1 flex-col gap-4 p-0 md:p-1 xl:p-4 sm:pt-0 h-[calc(100%-4rem)] min-w-0 max-w-full",
                            isMobile &&
                                "portrait:pb-[calc(4rem+env(safe-area-inset-bottom))]",
                        )}
                    >
                        <Outlet />
                    </div>
                </SidebarInset>
                <MobileBottomNav />
            </SidebarProvider>
        </>
    );
}
